import { ref, uploadBytes, type StorageError } from "firebase/storage";
import { firebaseStorage, firebaseAuth } from "@/helpers/firebase/firebase";
import { ImageSizes } from "@/enums/images-sizes";
import { CacheTypes } from "@/enums/cache-types";
import { fetchPicture } from "./PictureService";
import type { PictureResult, UploadPictureResult } from "@/types/picture";
import imageCompression from "browser-image-compression";
import { encodeImageToBlurhash } from "@/helpers/blurhash/encodeImageToBlurhash";
import { handleFirestorageException } from "@/helpers/exceptions/handler";

// 3 months TTL for group pictures (MD5 hash validation handles invalidation)
const GROUP_PICTURE_TTL = 90 * 24 * 60 * 60 * 1000;

export async function getGroupPictureFromFirestorage(
  groupId: string,
  groupPicturePath: string,
  format: ImageSizes = ImageSizes.HD,
): Promise<PictureResult> {
  const formatReference = ref(
    firebaseStorage,
    `groups/${groupId}/${groupPicturePath}${format}`,
  );
  const fallbackReference = ref(
    firebaseStorage,
    `groups/${groupId}/${groupPicturePath}`,
  );

  return fetchPicture({
    cacheType: CacheTypes.Group,
    cacheId: `${groupId}:${groupPicturePath}`,
    formatReference,
    fallbackReference,
    format,
    callerTtlMs: GROUP_PICTURE_TTL,
    setterTtlMs: GROUP_PICTURE_TTL,
  });
}

/**
 * Uploads a group picture with compression and blurhash generation
 * @param groupId - Group ID
 * @param file - File to upload
 * @returns Picture path and blurhash
 */
export async function uploadGroupPicture(
  groupId: string,
  file: File,
): Promise<UploadPictureResult> {
  try {
    const imageName = groupId + new Date().toISOString();
    const storagePath = `groups/${groupId}/group_pictures/${imageName}`;
    const groupPictureRef = ref(firebaseStorage, storagePath);

    // Compress main image
    const compressedFile = await imageCompression(file, {
      maxSizeMB: 1,
      maxWidthOrHeight: 3840,
      useWebWorker: true,
    });

    // Create super compressed version for blurhash
    const superCompressedFile = await imageCompression(compressedFile, {
      maxWidthOrHeight: 20,
      useWebWorker: true,
    });

    // Generate blurhash
    const blurHash = await encodeImageToBlurhash(
      URL.createObjectURL(superCompressedFile),
    );

    // Upload to Firebase Storage
    await uploadBytes(groupPictureRef, compressedFile, {
      customMetadata: { uploadedBy: firebaseAuth.currentUser!.uid },
    });

    return {
      picturePath: `group_pictures/${imageName}`,
      blurHash,
    };
  } catch (e) {
    handleFirestorageException({ e: e as StorageError });
    throw e;
  }
}
